import { Component, ElementRef, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { EmployeeQuery } from '@app/core/global-state/employees/employee.query';
import { EmployeeService } from '@app/core/global-state/employees/employee.service';
import { ActiveWorkCenterService } from '@app/core/workcenter';
import { EmployeeRegistrationDetailsService } from '@app/modules/employee-registration/services/employee-registration.service';
import { TranslateService } from '@ngx-translate/core';
import { ActiveEmployeeRegistrationViewModel, AvailableEmployee } from 'chronos-core-client';
import { AppSettingsQuery, LoadingNotificationService, LogService } from 'chronos-shared';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoginWindowComponent } from '../../login-window';
import { notificationTopic } from '@app/shared/utils';

@Component({
  selector: 'app-employee-registration-login',
  templateUrl: './employee-registration-login.component.html',
  styleUrls: ['./employee-registration-login.component.scss']
})
export class EmployeeRegistrationLoginComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchBox', { static: false }) private searchBox: ElementRef;
  @ViewChild('rfidBox', { static: false }) private rfidBox: ElementRef;

  public activeEmployeeRegistrations: ActiveEmployeeRegistrationViewModel[];
  public availableEmployees: AvailableEmployee[];
  public textQuery? = '';
  public rfid? = '';
  public maskedRfid: string = '';
  public readonly LOADING_TOPIC_LOGOUT_ = notificationTopic.employeeLogOut;
  private subscriptions = new Subscription();
  private workCenterId: number;
  private isRFIDEnabled: boolean;

  constructor(
    private employeeRegistrationDetailsService: EmployeeRegistrationDetailsService,
    private activeWorkCenterService: ActiveWorkCenterService,
    public employeeQuery: EmployeeQuery,
    public employeeService: EmployeeService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    public appSettingsQuery: AppSettingsQuery
  ) {}

  public ngOnInit(): void {
    this.workCenterId = this.activeWorkCenterService.getWorkCenterId();
    this.employeeService.getEmployeeRegistrationData();
    this.subscriptions.add(this.employeeRegistrationDetailsService.getEmployeeListNotifications(this.workCenterId).subscribe());
    this.getActiveEmployeeRegistrations();
    this.isRFIDEnabled = this.appSettingsQuery.enableLoginWithBadgeId();
    this.employeeService.updateFilters({
      isRFIDEnabled: this.isRFIDEnabled
    });
  }

  private getActiveEmployeeRegistrations(): void {
    this.employeeQuery.loggedInEmployees$
      .pipe(
        tap((activeEmployeeRegistrations) => {
          this.activeEmployeeRegistrations = activeEmployeeRegistrations;
        })
      )
      .subscribe();
  }

  public onFilterChange(): void {
    this.employeeService.updateFilters({
      textQuery: this.textQuery,
      rfid: this.rfid,
      isRFIDEnabled: this.isRFIDEnabled
    });
  }

  public onRfidInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;

    if (currentValue.length === 0) {
      this.rfid = '';
    } else if (currentValue.length === this.maskedRfid.length + 1) {
      const newChar = currentValue[currentValue.length - 1];
      this.rfid += newChar;
    } else if (currentValue.length > this.maskedRfid.length) {
      this.rfid += currentValue.replace(/\*/g, '');
    } else if (currentValue.length < this.maskedRfid.length) {
      this.rfid = this.rfid.slice(0, currentValue.length - this.maskedRfid.length);
    }

    this.maskedRfid = '*'.repeat(this.rfid.length);
    inputElement.value = this.maskedRfid;

    this.onFilterChange();
  }

  public onLoggedInEmployeeBlur(loggedInEmployee: ActiveEmployeeRegistrationViewModel): void {
    this.employeeRegistrationDetailsService.updateRegistration(loggedInEmployee, this.workCenterId).subscribe(
      () => null,
      () => {
        this.employeeService.getEmployeeRegistrationData();
      }
    );
  }

  public onLogOut(loggedInEmployee: ActiveEmployeeRegistrationViewModel): void {
    const workCenterId = this.activeWorkCenterService.getWorkCenterId();
    LoadingNotificationService.publish(this.LOADING_TOPIC_LOGOUT_ + loggedInEmployee.id, true);
    this.employeeRegistrationDetailsService.logOut(loggedInEmployee.employeeId, workCenterId).subscribe(() => {
      LogService.success('SUCCESS_MESSAGE.LOGGEDOUT_SUCCESSFULLY');
      LoadingNotificationService.publish(this.LOADING_TOPIC_LOGOUT_ + loggedInEmployee.id, false);
    });
  }

  public onLogIn(employee: AvailableEmployee): void {
    const modal = this.dialogService.open(LoginWindowComponent, {
      header: this.translateService.instant('EMPLOYEEREGISTRATION.EMPLOYEE_LOGIN'),
      data: {
        employee
      }
    });
    modal.onClose.subscribe((result) => {
      if (result) {
        this.rfid = '';
        this.maskedRfid = '';
        if (this.rfidBox) {
          this.rfidBox.nativeElement.value = '';
        }
        this.onFilterChange();
        return;
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngAfterViewInit(): void {
    this.subscriptions.add(
      this.employeeRegistrationDetailsService.setFocusSubject$.subscribe(() => {
        let element;
        if (this.appSettingsQuery.enableLoginWithBadgeId()) {
          element = this.rfidBox.nativeElement;
        } else {
          element = this.searchBox.nativeElement;
        }
        setTimeout(() => {
          element.focus();
        }, 10);
      })
    );
  }
}
